/**
 * EntityDetailModal — detail view for a single ClientMarginRow, opened
 * from FiTopClientsMarginTable. Shows revenue/cogs/margin summary and,
 * when available, the recent revenue trend aligned to periodLabels.
 */
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type { ClientMarginRow } from './fiTypes'
import { formatCurrency, formatCompact, formatPct, marginTone } from './fiFormat'

interface EntityDetailModalProps {
  entity: ClientMarginRow
  /** Period labels aligned oldest→newest with entity.trend (same length). */
  periodLabels?: string[]
  onClose: () => void
}

const OFFERING_LABEL: Record<ClientMarginRow['offeringMix'], string> = {
  package: 'Paquete',
  consulting: 'Consultoría',
  mixed: 'Mixto',
}

export default function EntityDetailModal({ entity, periodLabels, onClose }: EntityDetailModalProps) {
  const tone = marginTone(entity.marginPct)

  const chartData = (entity.trend || []).map((value, i) => ({
    period: periodLabels?.[periodLabels.length - (entity.trend?.length || 0) + i] ?? `#${i + 1}`,
    revenue: value,
  }))

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">{entity.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                entity.isIntragroup ? 'bg-teal-500/20 text-teal-300' : 'bg-slate-500/20 text-slate-300'
              }`}>
                {entity.isIntragroup ? 'Intra-grupo' : 'Externo'}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                entity.offeringMix === 'package' ? 'bg-blue-500/20 text-blue-300'
                  : entity.offeringMix === 'consulting' ? 'bg-purple-500/20 text-purple-300'
                  : 'bg-amber-500/20 text-amber-300'
              }`}>
                {OFFERING_LABEL[entity.offeringMix]}
              </span>
              {!entity.costKnown && (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-600/40 text-gray-400">
                  Costo incompleto
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-gray-400 text-xs">Revenue</p>
            <p className="text-xl font-bold text-white">{formatCurrency(entity.revenue)}</p>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-gray-400 text-xs">COGS</p>
            <p className="text-xl font-bold text-white">{formatCurrency(entity.cogs)}</p>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-gray-400 text-xs">Utilidad Bruta</p>
            <p className={`text-xl font-bold ${entity.grossProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(entity.grossProfit)}
            </p>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-gray-400 text-xs">Margen</p>
            <p className={`text-xl font-bold ${tone.text}`}>{formatPct(entity.marginPct)}</p>
          </div>
        </div>

        {chartData.length >= 2 ? (
          <div className="px-6 pb-6">
            <p className="text-gray-400 text-xs uppercase tracking-wide mb-2">Tendencia de Revenue</p>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="entityRevenueFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" />
                  <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9ca3af' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9ca3af' }}
                    tickFormatter={v => formatCompact(v)} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#fff', fontWeight: 600 }}
                    formatter={(value: number) => [formatCurrency(value), 'Revenue']}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} fill="url(#entityRevenueFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="px-6 pb-6 text-xs text-gray-500">Sin suficiente historial para graficar tendencia.</div>
        )}
      </div>
    </div>
  )
}
